/**
 * @file Stun an enemy and knock its weapon out of its hands.
 *
 * Shared by thrown-weapon impacts and the player dash so both paths stay
 * identical: same stun length, same floor drop, same AI interrupt. No damage —
 * the move buys time, it does not kill.
 */

import {
  AIBrain,
  AIState,
  RangedAI,
  Transform,
  Weapon
} from '../components/index.js';
import { dropWeapon } from '../entities/WeaponDrop.js';

/**
 * Stuns a target and, if it is armed, drops its weapon at its feet.
 *
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @param {import('../ecs/Entity.js').Entity} target Enemy to stun.
 * @param {number} [stunDuration] Seconds of stun; defaults to
 *   `config.throwing.stunDuration`.
 * @returns {boolean} True when a weapon was removed and dropped.
 */
export function stunAndDisarm(ctx, target, stunDuration) {
  if (!target?.alive) return false;

  const brain = target.get(AIBrain);
  const ranged = target.get(RangedAI);
  const stun = brain ?? ranged;
  if (!stun) return false;

  const duration = stunDuration ?? ctx.config.throwing.stunDuration;
  stun.stunRemaining = duration;
  // First star ring on the very next frame, not after a full interval.
  stun.stunFxCooldown = 0;
  if (brain) brain.setState(AIState.STUNNED);
  if (ranged) ranged.wantsToFire = false;

  const weapon = target.get(Weapon);
  const transform = target.get(Transform);
  if (!weapon || !transform) return false;

  target.remove(Weapon);
  if (brain) brain.disarm();
  dropWeapon(ctx, transform.position.x, transform.position.z, weapon);
  return true;
}
